import type { TMatterRetriever } from "../../services/matter-retriever";
import type { TMatterAssigner } from "../../services/assign-to-matter-service";
import type { TMatter } from "../../models/matter-model";
import type { Props as MatterPresentationalProps } from "../Matter/MatterPresentational";
import { TaskpanePresentational } from "./TaskpanePresentational";
import { SearchContainer } from "../Search/SearchContainer";
import { useNotificationStore } from "../Notifications/notification-store";
import { useItemStore } from "../../mailitem/mail-item-store";
import { LogoutContainer } from "../Logout/LogoutContainer";
import { SmokeballAuthenticator, TSmokeballTier } from "../../services/smokeball-authenticator";
import { ErrorType } from "../../helpers/ErrorType";

export type Props = {
  tier: TSmokeballTier;
  assignedMatter: TMatter | null;
  matterAssigner: TMatterAssigner;
  matterRetriever: TMatterRetriever;
  isCheckingForAssignedMatter: boolean;
  authenticator: SmokeballAuthenticator;

  openAttachFiles?: (matterId: string) => void;
  setAssignedMatter: (m: TMatter | null) => void;
};

export const TaskpaneComposeContainer = (props: Props) => {
  const errorNotification = useNotificationStore((s) => s.errorNotification);
  const mailItem = useItemStore((s) => s.wrappedMailItem);

  const onMatterSelected = async (args: Parameters<NonNullable<MatterPresentationalProps["onMatterSelected"]>>[0]) => {
    const matter = args.isSelected ? args.matter : null;

    if (mailItem) {
      const matterId = matter?.matterId ?? null;
      try {
        if (matterId) {
          const assignResult = await props.matterAssigner
            .assignDraftToMatter(mailItem, matterId)
            .catch((e) => errorNotification("Assign draft failed", e));
          const assignErr = assignResult && assignResult.err;
          if (assignErr) {
            if (assignErr === ErrorType.NoMatterPermission) {
              errorNotification("You currently don't have permission to access this Matter");
            } else if (assignErr === ErrorType.Unauthorized) {
              errorNotification("Your authorization has expired. Please sign in again");
            } else {
              console.error("Error Assigning Draft", assignErr);
              errorNotification("Assign draft failed", assignErr);
            }
            return;
          }
        } else {
          props.matterAssigner
            .unAssignDraftFromMatter(mailItem)
            .catch((e) => errorNotification("unassign draft failed", e));
        }

        props.setAssignedMatter(matter);
      } catch (err) {
        errorNotification("Error assigning email to matter", err);
      }
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 max-h-full min-h-0">
        <TaskpanePresentational
          tier={props.tier}
          onMatterSelected={onMatterSelected}
          assignedMatter={props.assignedMatter || undefined}
          isCheckingForAssignedMatter={props.isCheckingForAssignedMatter}
          openAttachFiles={(props.assignedMatter && props.openAttachFiles) || undefined}
          searchContent={
            props.assignedMatter ? null : (
              <SearchContainer
                tier={props.tier}
                onMatterSelected={onMatterSelected}
                matterRetriever={props.matterRetriever}
              />
            )
          }
        />{" "}
      </div>
      <div className="flex flex-col justify-end mt-auto pt-2">
        <LogoutContainer authenticator={props.authenticator} />
      </div>
    </div>
  );
};
